/**
 * useExecutionConfig.ts
 * Hook que carrega a configuração de execução + dados salvos para uma task.
 * Prioridade: tabelas v2 → metadata fallback.
 */
import { useQuery } from '@tanstack/react-query';
import { loadExecutionConfig, type StageExecutionConfig } from '../../shared/services/executionConfigService';
import { loadFieldResponses, loadChecklistResponses, loadAttachments } from '../../shared/services/executionDataService';
import type { TaskDetailItem } from '../../shared/types/task';

export interface ExecutionConfigResult {
  /** Configuração completa da etapa */
  config: StageExecutionConfig | null;
  /** Dados salvos de campos (do banco) */
  savedFormData: Record<string, unknown>;
  /** Dados salvos de checklist (do banco) */
  savedChecklistData: Record<string, boolean>;
  /** Anexos salvos */
  savedAttachments: { id: string; fileName: string; fileUrl: string; fileType: string | null; createdAt: string }[];
  /** Loading state */
  isLoading: boolean;
  /** Se dados vieram das tabelas v2 */
  fromV2Tables: boolean;
}

export function useExecutionConfig(
  task: TaskDetailItem | null,
  organizationId: string | null,
): ExecutionConfigResult {
  const taskId = task?.id ?? null;
  const stageId = task?.stageId ?? null;
  const orgId = organizationId ?? '';

  // 1. Carregar config da etapa (campos, checklist, regras)
  const configQuery = useQuery({
    queryKey: ['execution-config', stageId, orgId],
    enabled: Boolean(stageId && orgId),
    staleTime: 5 * 60_000, // 5 min — config raramente muda durante execução
    queryFn: () =>
      loadExecutionConfig(stageId!, orgId, {
        instruction: task?.instruction,
        completionType: task?.completionType,
        formFields: task?.formFields,
        checklist: task?.checklist,
        executionConfig: task?.metadata?.executionConfig as any,
      }),
  });

  // 2. Carregar respostas salvas (dados do operador)
  const fieldDataQuery = useQuery({
    queryKey: ['execution-field-data', taskId, orgId],
    enabled: Boolean(taskId && orgId),
    staleTime: 30_000,
    queryFn: () => loadFieldResponses(taskId!, orgId),
  });

  const checklistDataQuery = useQuery({
    queryKey: ['execution-checklist-data', taskId, orgId],
    enabled: Boolean(taskId && orgId),
    staleTime: 30_000,
    queryFn: () => loadChecklistResponses(taskId!, orgId),
  });

  const attachmentsQuery = useQuery({
    queryKey: ['execution-attachments', taskId, orgId],
    enabled: Boolean(taskId && orgId),
    staleTime: 30_000,
    queryFn: () => loadAttachments(taskId!, orgId),
  });

  return {
    config: configQuery.data ?? null,
    savedFormData: fieldDataQuery.data ?? {},
    savedChecklistData: checklistDataQuery.data ?? {},
    savedAttachments: attachmentsQuery.data ?? [],
    isLoading: configQuery.isPending || fieldDataQuery.isPending || checklistDataQuery.isPending,
    fromV2Tables: configQuery.data?.fromV2Tables ?? false,
  };
}
